import React, { useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useRecoilCallback } from 'recoil'
import debounce from 'lodash.debounce'
import PropTypes from 'prop-types'
import {
  useAsyncSelect,
  Select,
  SELECT_VARIANT_WITH_CHECKBOX
} from 'mailup-web-components'

import {
  getGroupById,
  getGroups,
  getTestGroups
} from 'Pages/AbTestWizard/recoil/callbacks/asyncSearch'
import { errorCallback } from 'Pages/AbTestWizard/recoil/callbacks/callbacks'

import { INPUT_GROUPS_MAX_ITEMS } from 'Constants/'
import {
  GROUP_TYPE_TEST,
  RECIPIENTS_TEST_GROUP
} from 'Pages/AbTestWizard/const'

const IncludeGroups = ({ handleSelectChange, listOptions, testGroup }) => {
  const intl = useIntl()

  const onError = useRecoilCallback(errorCallback, [])

  const getInitialItems = () =>
    testGroup === RECIPIENTS_TEST_GROUP ? getTestGroups() : getGroups()

  const getItemsByTerm = searchTerm =>
    RECIPIENTS_TEST_GROUP === GROUP_TYPE_TEST
      ? getTestGroups(searchTerm)
      : getGroups(searchTerm)

  const hints = {
    loading: intl.formatMessage({ id: 'Load.MailupCommon.InputStatusLoading' }),
    moreResults: intl.formatMessage({
      id: 'Load.Filters.ActivityMessagesMoreResults'
    }),
    noResults: intl.formatMessage({ id: 'Load.Common.NoResultChosen' })
  }

  const {
    options,
    loading,
    hint,
    hintNoResults,
    onSearch,
    onChange,
    onExpandCollapse
  } = useAsyncSelect({
    initialValue: listOptions,
    getInitialItems,
    getItemsByTerm,
    getItemById: getGroupById,
    onError,
    hints,
    maxItems: INPUT_GROUPS_MAX_ITEMS
  })

  const withNoGroup = useMemo(() => {
    return [
      {
        value: 0,
        label: intl.formatMessage({ id: 'Load.Filters.NoGroupOption' })
      },
      ...options
    ]
  }, [options])

  const handleChange = value => {
    handleSelectChange(value)
    onChange(value)
  }

  const { current: debouncedOnSearch } = useRef(debounce(onSearch, 350))

  const handleSearch = s => {
    debouncedOnSearch.cancel()
    debouncedOnSearch(s)
    return s
  }

  return (
    <Select
      multiple
      searchable
      variant={SELECT_VARIANT_WITH_CHECKBOX}
      value={listOptions}
      hint={hint}
      hintNoResults={hintNoResults}
      onChange={handleChange}
      loading={loading}
      options={withNoGroup}
      onSearch={handleSearch}
      onExpandCollapse={onExpandCollapse}
      placeholder={intl.formatMessage({
        id: 'Load.SearchRecipients.IncludeGroupsPlaceholder'
      })}
    />
  )
}

IncludeGroups.propTypes = {
  handleSelectChange: PropTypes.func.isRequired,
  listOptions: PropTypes.array.isRequired,
  testGroup: PropTypes.number.isRequired
}

export default IncludeGroups
